import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ProPlan } from './components/ProPlan';
import { EnterprisePlan } from './components/EnterprisePlan';
import CloseButton from '@ui/CloseButton';
import { usePaywallStore } from './paywall.store';
import { PaywallSubmitConfirmed } from './PaywallSubmitConfirmed';
import amplitudeLog from '@client/amplitude';
import { AMP_PAYWALL_CLOSE } from '@shared/constants';
import { ActiveProjects } from '@shared/models/paywall.model';
import { PaywallDerivedSync } from './PaywallDerivedSync';

export const Paywall = () => {
  const { open, closeDialog, setPlanData, planData } = usePaywallStore();
  const { classes } = useStyles();

  // Single subscription to org + projects; updates store so useOpenPaywallDialog stays cheap
  const handleClose = () => {
    amplitudeLog(AMP_PAYWALL_CLOSE);
    closeDialog();
  };

  if (planData) {
    const { plan, activeProjects } = planData;
    return (
      <>
        <PaywallDerivedSync />
        <PaywallSubmitConfirmed
          open={true}
          onClose={() => setPlanData(undefined)}
          title={
            plan === 'pro'
              ? 'Pro upgrade request sent'
              : 'Enterprise request sent'
          }
          body={
            plan === 'pro' ? (
              <ProPlanConfirmContent activeProjects={activeProjects} />
            ) : (
              <EnterprisePlanConfirmContent />
            )
          }
        />
      </>
    );
  }

  return (
    <>
      <PaywallDerivedSync />
      <Dialog open={open} onClose={handleClose} maxWidth={false}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <CloseButton onClick={handleClose} />
        </Box>
        <DialogContent className={classes.dialogContent}>
          <ProPlan />
          <EnterprisePlan />
        </DialogContent>
      </Dialog>
    </>
  );
};

const ProPlanConfirmContent = ({
  activeProjects,
}: {
  activeProjects: ActiveProjects;
}) => {
  return (
    <>
      <Typography
        sx={{
          paddingBottom: 4,
        }}
      >
        Thanks for your interest in the <b>Pro plan</b>.
      </Typography>
      <Typography>
        We’ve received your request for <b>Pro</b>.
      </Typography>
      <Typography
        sx={{
          paddingBottom: 4,
        }}
      >
        (up to <b>{activeProjects}</b> active projects, billed annually).
      </Typography>
      <Typography>
        We’ll contact you to confirm the details before updating your
        subscription.
      </Typography>
    </>
  );
};

const EnterprisePlanConfirmContent = () => {
  return (
    <>
      <Typography
        sx={{
          paddingBottom: 4,
        }}
      >
        Thanks for your interest in the <b>Enterprise plan</b>.
      </Typography>
      <Typography>
        We’ve received your request and will get in touch to learn more about
        your portfolio, integrations, and support needs.
      </Typography>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  dialogContent: {
    display: 'flex',
    gap: theme.spacing(4),
  },
}));
